import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Star, Award, CheckCircle, ArrowRight } from 'lucide-react';

// ----------------------
// Interfaces
// ----------------------
interface Product {
  id: number;
  categoryId: number; 
  name: string;
  categoryName: string;
  imageUrl: string;
  price: number;
  rating: number; 
  reviewCount: number;
  discount: string; 
}

// ----------------------
// ProductCard Component (UPDATED TO MATCH BESTSELLER DESIGN)
// ----------------------
const ProductCard: React.FC<{ product: Product; index: number }> = ({ product, index }) => {
  const formatPrice = (price: number) => 
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0 }).format(price);

  return (
    <article 
        className="group relative bg-white border border-gray-200 rounded-2xl overflow-hidden hover:shadow-2xl transition-all duration-300 flex flex-col h-full hover:-translate-y-1 cursor-pointer"
        itemScope 
        itemType="https://schema.org/Product"
    >
      {/* 1. Image Area */}
      <div className="relative w-full aspect-[4/3] bg-gray-50 flex items-center justify-center overflow-hidden p-6">
        
        {/* Category Badge (Replaces Ranking Badge) */}
        <div className="absolute top-0 left-0 bg-teal-600 text-white text-[10px] font-bold px-3 py-1.5 rounded-br-xl z-20 shadow-sm flex items-center gap-1">
           <Award className="w-3 h-3" />
           {product.categoryName}
        </div>

        <img 
            src={product.imageUrl} 
            alt={product.name} 
            loading="lazy"
            className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-110 mix-blend-multiply"
            onError={(e) => {e.currentTarget.src = 'https://via.placeholder.com/300?text=TradyFrog'}}
            itemProp="image"
        />
        
        {/* Hover Overlay */}
        <div className="absolute inset-0 bg-teal-900/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />

        {/* Action Buttons (Slide Up Effect) */}
        <div className="absolute inset-x-0 bottom-4 px-4 flex items-center justify-center 
                        translate-y-12 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 
                        transition-all duration-300 ease-out z-20">
          <a 
            href={`/categories/${product.categoryId}`} 
            className="w-full text-center py-2.5 text-xs font-bold rounded-lg text-white bg-teal-600 hover:bg-teal-700 shadow-lg transition-colors flex items-center justify-center gap-2"
          >
            VIEW CATEGORY <ArrowRight className="w-3 h-3" />
          </a>
        </div>
      </div>

      {/* 2. Content Area */}
      <div className="p-4 flex flex-col flex-grow border-t border-gray-50">
        
        {/* Rating & Verified Badge */}
        <div className="flex justify-between items-start mb-2">
            <div className="flex items-center gap-1 text-[10px] text-gray-500 font-medium uppercase tracking-wide">
                VERIFIED <CheckCircle className="w-3 h-3 text-teal-500" />
            </div>
            
            <div className="flex items-center gap-1 bg-amber-50 border border-amber-100 px-1.5 py-0.5 rounded text-[10px] font-bold text-amber-700">
                {product.rating} <Star className="w-3 h-3 fill-amber-500 text-amber-500" />
            </div>
        </div>
        
        <h3 className="text-sm font-semibold text-gray-900 line-clamp-2 leading-snug mb-3 group-hover:text-teal-600 transition-colors">
          <a href={`/categories/${product.categoryId}`} itemProp="name">{product.name}</a>
        </h3>
        
        <div className="mt-auto pt-3 border-t border-dashed border-gray-200 flex items-center justify-between">
          <div className="flex flex-col">
             <span className="text-lg font-bold text-gray-900 leading-none">
                {formatPrice(product.price)}
             </span>
          </div>
          
          <div className="bg-red-50 text-red-600 text-[10px] font-bold px-2 py-1 rounded border border-red-100">
             {product.discount || 'BEST PRICE'}
          </div>
        </div>
      </div>
    </article>
  );
};

// ----------------------
// Main Layout Component
// ----------------------
const FullPageLayout: React.FC = () => {
    const sidebarImageUrl = "https://mir-s3-cdn-cf.behance.net/project_modules/fs/ce1583179308059.64f769e8eec99.jpg"; 
    
    const [products, setProducts] = useState<Product[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const res = await axios.get('http://localhost:2026/api/products/top-categories');
                setProducts(res.data);
            } catch (error) {
                console.error("Error fetching top categories:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    return (
        <section className="mt-12 mb-16 max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
            
            {/* Header */}
            <header className="flex flex-col sm:flex-row justify-between items-end mb-8 border-b border-gray-100 pb-4">
                <div>
                    <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-gray-900">
                        Top Categories <span className="text-teal-600">& Products</span>
                    </h2>
                    <p className="text-sm text-gray-500 mt-1">Explore high-demand categories for your business</p>
                </div>
            </header>

            {/* Main Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                
                {/* --- LEFT SIDEBAR (Unchanged structure, just updated theme colors) --- */}
                <aside className="lg:col-span-3 h-full">
                    <div className="group relative bg-white border border-gray-200 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden flex flex-col h-full min-h-[500px]">
                        
                        {/* Image Area */}
                        <div className="h-1/2 lg:flex-grow relative overflow-hidden">
                            <img 
                                src={sidebarImageUrl} 
                                alt="Trady Frog B2B Hub"
                                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                                loading="lazy"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                        </div>

                        {/* Content Area */}
                        <div className="p-6 flex flex-col justify-end relative bg-white z-10">
                             {/* Floating Icon */}
                             <div className="absolute -top-8 right-6 p-3 bg-teal-50 rounded-full border-4 border-white shadow-md text-2xl text-teal-600">
                                🚀
                             </div>

                            <div className="mb-6 mt-4">
                                <h2 className="text-3xl font-extrabold text-gray-900 mb-2">
                                    Trusted <span className="text-teal-600">B2B Hub</span>
                                </h2>
                                <p className="text-base text-gray-600 leading-relaxed">
                                    Join our network of verified sellers. List your products and connect directly with businesses.
                                </p>
                            </div>
                            
                            <button className="w-full py-4 px-4 bg-gray-900 text-white font-bold text-sm rounded-xl shadow-lg 
                                               hover:bg-teal-600 transition-all duration-300 transform active:scale-95 flex items-center justify-center gap-2">
                                Direct Join with Seller
                                <ArrowRight className="w-5 h-5" />
                            </button>
                        </div>
                    </div>  
                </aside>

                {/* --- RIGHT CONTENT --- */}
                <main className="lg:col-span-9 w-full">
                    {loading ? (
                        <div className="w-full h-64 flex items-center justify-center">
                            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-teal-600"></div>
                        </div>
                    ) : (
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6 h-full"> 
                            {products.map((product, index) => (
                                <ProductCard key={product.id} product={product} index={index} />
                            ))}
                        </div>
                    )}
                </main>
            </div>
        </section>
    );
};

export default FullPageLayout;